import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronDown, Flag } from 'lucide-react'
import { PlayHeadline } from '@/components/brand/PlayHeadline'
import { SiteHeader } from '@/components/SiteHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { ExploreEventCard, ExploreEventSkeletons } from '@/components/explore/ExploreEventCard'
import { Button, ButtonLink, EmptyState, Notice } from '@/components/play'
import { isViewer } from '@/components/auth/isViewer'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useAuth } from '@/state/AuthContext'
import { api } from '@/util/api'
import type { ExploreEvent, ExploreEventsResponse } from '@/types/explore'
import '@/components/site-page.css'
import './ExplorePage.css'

/** Listed events on now and to come, soonest first, a page at a time (GET /public/events, section 6.2). */
function fetchEvents(cursor?: string) {
  return api.get<ExploreEventsResponse>('/public/events', cursor ? { cursor } : undefined)
}

/** Where focus goes once a load that the person asked for has finished: an event's card, or the empty state's
 *  Host a watch-along. */
type FocusTarget = { event: string } | 'empty' | null

/**
 * Upcoming events (/explore): every listed event on now or still to come, as cards linking to their public pages,
 * with Show more for the next page. Nothing listed asks people to host a watch-along; a host can also go to their
 * events to list one. The control that retries a failed load stays in place, busy, while it runs, so focus never
 * falls to the page. Docs: docs/features/explore.md.
 */
export function ExplorePage() {
  const { isAuthenticated, authReady, user, organization } = useAuth()
  const host = isAuthenticated && authReady && !isViewer(organization, user)
  const [events, setEvents] = useState<ExploreEvent[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // retrying keeps the failure notice, with its Try again busy, until the retry answers.
  const [status, setStatus] = useState<'loading' | 'retrying' | 'ready' | 'failed'>('loading')
  const [attempt, setAttempt] = useState(0)
  const [failures, setFailures] = useState(0)
  const [more, setMore] = useState<'idle' | 'loading' | 'failed'>('idle')
  const links = useRef(new Map<string, HTMLAnchorElement>())
  const hostAction = useRef<HTMLAnchorElement>(null)
  const focusOnLoad = useRef<FocusTarget>(null)
  useDocumentTitle('Upcoming events · StreamBridge')

  useEffect(() => {
    let cancelled = false
    fetchEvents()
      .then((res) => {
        if (cancelled) return
        const loaded = res.data ?? []
        // A retry's Try again goes with its notice, so focus moves on to what loaded.
        if (attempt > 0) focusOnLoad.current = loaded[0] ? { event: loaded[0].id } : 'empty'
        setEvents(loaded)
        setNextCursor(res.meta?.next_cursor ?? null)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setFailures((count) => count + 1)
        setStatus('failed')
      })
    return () => { cancelled = true }
  }, [attempt])

  // A load the person asked for hands focus to what it brought: Show more to its first event, even when the button goes
  // with the last page; a retry to the first event, or to Host a watch-along when nothing is listed. Before paint, so
  // focus never rests on the page while the control that had it goes.
  useLayoutEffect(() => {
    const target = focusOnLoad.current
    if (!target) return
    focusOnLoad.current = null
    ;(target === 'empty' ? hostAction.current : links.current.get(target.event))?.focus()
  }, [events, status])

  const retry = () => {
    setStatus('retrying')
    setAttempt((current) => current + 1)
  }

  const showMore = useCallback(async () => {
    if (!nextCursor) return
    setMore('loading')
    try {
      const res = await fetchEvents(nextCursor)
      const added = (res.data ?? []).filter((event) => !events.some((shown) => shown.id === event.id))
      focusOnLoad.current = added[0] ? { event: added[0].id } : null
      setEvents([...events, ...added])
      setNextCursor(res.meta?.next_cursor ?? null)
      setMore('idle')
    } catch {
      setMore('failed')
    }
  }, [events, nextCursor])

  return (
    <div className="play-theme site-page play-site">
      <a className="site-page__skip" href="#explore-main">Skip to content</a>
      <SiteHeader />
      <main id="explore-main" className="site-page__main">
        <div className="site-page__intro">
          <p className="site-page__eyebrow">Upcoming events</p>
          <h1 className="site-page__title"><PlayHeadline words={['On now.', 'Up next.']} /></h1>
          <p className="site-page__lead">Pick a race. Bring your people.</p>
        </div>

        {status === 'loading' && <ExploreEventSkeletons />}

        {(status === 'failed' || status === 'retrying') && (
          // An alert once a retry has failed too. The keyed message is new text each time, so every failure is
          // announced, while the notice and its Try again stay mounted.
          <Notice tone="error" alert={failures > 1} action={<Button loading={status === 'retrying'} onClick={retry}>Try again</Button>}>
            <span key={failures}>We couldn’t load upcoming events.</span>
          </Notice>
        )}

        {status === 'ready' && events.length === 0 && (
          <EmptyState
            icon={<Flag />}
            title="Nothing on the start line yet"
            description="Races show up here once their hosts list them."
            action={
              <>
                <ButtonLink to="/host" ref={hostAction}>Host a watch-along</ButtonLink>
                {host && <ButtonLink to="/app" variant="secondary">Your events</ButtonLink>}
              </>
            }
          />
        )}

        {status === 'ready' && events.length > 0 && (
          <>
            {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
            <ul className="explore-grid" role="list" aria-label="Upcoming events">
              {events.map((event) => (
                <li key={event.id}>
                  <ExploreEventCard
                    event={event}
                    ref={(node) => {
                      if (node) links.current.set(event.id, node)
                      else links.current.delete(event.id)
                    }}
                  />
                </li>
              ))}
            </ul>
            {/* Show more stays mounted under a failure, as the way to try again, so it keeps focus. */}
            {more === 'failed' && (
              <Notice tone="error" alert className="play-explore__more-error">We couldn’t load more events. Try again.</Notice>
            )}
            {nextCursor && (
              <div className="play-explore__more">
                <Button variant="secondary" loading={more === 'loading'} onClick={showMore}>
                  <ChevronDown aria-hidden="true" />
                  Show more
                </Button>
              </div>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
